import type { MutableRefObject } from "react";
import { useRef, useState } from "react";

import { t } from "i18next";

import {
  DropdownPanel,
  FieldTypePill,
  Icon,
  MenuOption,
} from "@smartsuite/react-ui/lib";
import { SolutionColorVariables } from "@smartsuite/types";
import type {
  ConditionFieldDescription,
  OutputReference,
} from "@smartsuite/automation-engine-api";

import { useTheme } from "../../../../../../../stores";
import { getLocalCssColorVariables } from "../../../../../../../utils/cssVariablesUtils";
import { ConditionOptions } from "../ConditionOptions";
import { SubConditionOptions } from "../SubConditionOptions";
import { ConditionSourceOptions } from "./ConditionSourceOptions";
import { buildLeftOutputReference } from "./build-left-output-reference";
import { matchesLeftSource } from "./matches-left-source";
import {
  StyledFieldLabel,
  StyledLeftControl,
  StyledPillWrapper,
} from "./ConditionLeftSelector.style";
import type { LeftSource } from "./types";

type Tab = "sources" | "fields" | "sub-menu";

export const ConditionLeftSelector: React.FC<{
  sources: LeftSource[];
  outputReference?: OutputReference;
  selectedLabel?: string;
  selectedIcon?: string;
  /** Source context ("Trigger: Record created / In Tasks") rendered by the
   * pill's hover tooltip -- same shape the right-side value pills use. */
  selectedTooltip?: { title: string; text?: string };
  onChange: (outputReference: OutputReference) => void;
}> = ({
  sources,
  outputReference,
  selectedLabel,
  selectedIcon,
  selectedTooltip,
  onChange,
}) => {
  const theme = useTheme();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("sources");
  const [activeSource, setActiveSource] = useState<LeftSource | undefined>();
  const [nestedOptions, setNestedOptions] = useState<
    ConditionFieldDescription[]
  >([]);
  const [submenuTitle, setSubmenuTitle] = useState("");

  const openPanel = (): void => {
    setTab("sources");
    setOpen(true);
  };

  const pickField = (conditionFieldId: string): void => {
    if (activeSource === undefined) return;
    onChange(buildLeftOutputReference(activeSource, conditionFieldId));
    setOpen(false);
  };

  // Highlight only in the list of the source the reference was picked from
  // (STR-14248): same-table sources share conditionFieldIds, so the id alone
  // would light the field up in every source's list.
  const highlightedFieldId =
    activeSource !== undefined &&
    matchesLeftSource(activeSource, outputReference)
      ? (outputReference?.outputId ?? "")
      : "";

  const hasSelection =
    outputReference !== undefined && selectedLabel !== undefined;
  const controlLabel = hasSelection
    ? selectedLabel
    : t("automation-form.select-field");

  return (
    <div ref={buttonRef}>
      {/* Reference-mode left control: a compact input matching the neighbouring
          operator/value inputs, showing the selected field name (or a
          placeholder) with a persistent blue "+" that (re)opens the source
          picker -- mirrors the right-side value input per the design. */}
      <StyledLeftControl
        data-testid="condition-left-selector"
        role="button"
        tabIndex={0}
        aria-label={controlLabel}
        $open={open}
        onClick={openPanel}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPanel();
          }
        }}
      >
        {hasSelection ? (
          /* Selected field renders as the same FieldTypePill (with the same
             advanced tooltip) the right-side value pills use, so both sides of
             the condition read identically. */
          <StyledPillWrapper>
            <FieldTypePill
              icon={selectedIcon}
              title={selectedLabel}
              tooltip={selectedTooltip}
            />
          </StyledPillWrapper>
        ) : (
          <StyledFieldLabel $placeholder>{controlLabel}</StyledFieldLabel>
        )}
        {/* Same `plus-circle` icon the right-side value input uses, so both
            sides render identically. */}
        <Icon
          name="plus-circle"
          size="S"
          color={open ? SolutionColorVariables.sc3 : SolutionColorVariables.sc1}
        />
      </StyledLeftControl>

      <DropdownPanel
        my="left top+2"
        at="left bottom+2"
        minWidth={200}
        portalStyles={getLocalCssColorVariables(theme)}
        isActive={open}
        targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
        onClickOutside={() => setOpen(false)}
      >
        {tab === "sources" && (
          <ConditionSourceOptions
            sources={sources}
            onSelect={(source) => {
              setActiveSource(source);
              setTab("fields");
            }}
          />
        )}
        {tab === "fields" && activeSource !== undefined && (
          <>
            <MenuOption
              iconName="arrow-left-1"
              ariaLabel={t("general.back")}
              onClick={() => setTab("sources")}
            >
              {activeSource.name}
            </MenuOption>
            <ConditionOptions
              condition={{
                conditionFieldId: highlightedFieldId,
              }}
              conditionFields={activeSource.conditionFields}
              onChange={(next) => pickField(String(next.conditionFieldId))}
              goSubMenu={(options, label) => {
                setNestedOptions(options);
                setSubmenuTitle(label);
                setTab("sub-menu");
              }}
            />
          </>
        )}
        {tab === "sub-menu" && (
          <SubConditionOptions
            condition={{ conditionFieldId: highlightedFieldId }}
            conditionFields={nestedOptions}
            title={submenuTitle}
            onChange={(next) => pickField(String(next.conditionFieldId))}
            onNavigateBack={() => setTab("fields")}
          />
        )}
      </DropdownPanel>
    </div>
  );
};
